import { useCallback, useEffect, useMemo, useRef, useState, type WheelEvent, type MouseEvent } from 'react'
import type { StoryboardSegmentPreviewEntry } from '../../types'
import PreviewEntryCard from '../PreviewEntryCard'
import type { TimelineSelection } from './useTimelineSelection'
import { buildTaskStatusChipProps, buildTaskStatusDescription, clampProgress } from './statusUtils'
import { Progress } from '@/components/ui/progress'
import SegmentStatusChip from './SegmentStatusChip'

interface SegmentInfoPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  arrowRef: HTMLDivElement | null;
  setArrowRef: (element: HTMLDivElement | null) => void;
  onClose: () => void;
  selection: TimelineSelection;
  mutating: boolean;
  onPreview: (entry: StoryboardSegmentPreviewEntry) => void;
  onAdopt: (entry: StoryboardSegmentPreviewEntry) => void;
  onOpenContextMenu: (
    entry: StoryboardSegmentPreviewEntry,
    event: MouseEvent<HTMLElement>
  ) => void;
}

const SegmentInfoPopover: React.FC<SegmentInfoPopoverProps> = ({
  open,
  anchorEl,
  arrowRef,
  setArrowRef,
  onClose,
  selection,
  mutating,
  onPreview,
  onAdopt,
  onOpenContextMenu,
}) => {
  const previewCarouselRef = useRef<HTMLDivElement | null>(null);
  const handlePreviewCarouselWheel = useCallback(
    (event: WheelEvent<HTMLDivElement>) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
        return;
      }
      event.preventDefault();
      const target = event.currentTarget;
      target.scrollLeft += event.deltaY;
    },
    []
  );

  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top')
  const [horizontalOffset, setHorizontalOffset] = useState(0)

  useEffect(() => {
    if (!open || !anchorEl) return
    const rect = anchorEl.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const availableTopSpace = rect.top
    const nextPlacement: 'top' | 'bottom' = availableTopSpace < 320 ? 'bottom' : 'top'
    setPlacement(nextPlacement)

    const preferredWidth = viewportWidth >= 640 ? 480 : 360
    const popoverWidth = Math.min(preferredWidth, viewportWidth - 48)
    const baseLeft = rect.left + rect.width / 2
    const halfWidth = popoverWidth / 2
    const minBoundary = 24
    const maxBoundary = viewportWidth - 24
    let offset = 0
    if (baseLeft - halfWidth < minBoundary) {
      offset = minBoundary - (baseLeft - halfWidth)
    } else if (baseLeft + halfWidth > maxBoundary) {
      offset = maxBoundary - (baseLeft + halfWidth)
    }
    setHorizontalOffset(offset)
    setPosition({ top: nextPlacement === 'top' ? rect.top : rect.bottom, left: baseLeft })
  }, [open, anchorEl])

  useEffect(() => {
    if (!open) return
    const closeOnAway = (evt: globalThis.MouseEvent) => {
      evt.stopPropagation()
      onClose()
    }
    window.addEventListener('mousedown', closeOnAway, { capture: true })
    return () => window.removeEventListener('mousedown', closeOnAway, { capture: true } as any)
  }, [open, onClose])

  const { infoSegment, previewEntries, selectedPreviewId, activePreviewId, tasks } = selection;
  if (!infoSegment) {
    return null;
  }

  if (!open || !position) return null

  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      <div
        className="pointer-events-auto rounded-md border bg-popover text-popover-foreground shadow-lg p-3 w-[360px] sm:w-[480px] max-w-[calc(100vw-48px)] relative"
        style={{ top: position.top, left: position.left + horizontalOffset, transform: placement === 'top' ? 'translate(-50%, calc(-100% - 12px))' : 'translate(-50%, 12px)', position: 'absolute' as const }}
      >
        <div
          ref={setArrowRef}
          className={`absolute left-1/2 -translate-x-1/2 ${placement === 'top' ? '-bottom-2' : '-top-2'}`}
          style={{ left: `calc(50% - ${horizontalOffset}px)` }}
        >
          <div className="w-3.5 h-3.5 rotate-45 bg-popover border border-border" />
        </div>
        <div className="space-y-2">
          {tasks.length > 0 ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">任务进度</div>
                <div className="text-[11px] text-muted-foreground">{tasks.length} 个任务进行中</div>
              </div>
              <div className="space-y-1.5">
                {tasks.map((task, index) => {
                  const progressValue = clampProgress(task.progress)
                  const statusChip = buildTaskStatusChipProps(task.status)
                  return (
                    <div
                      key={task.taskId}
                      className="rounded-md border bg-muted/40 p-2.5 flex flex-col gap-1"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold truncate">
                          {tasks.length > 1 ? `任务 ${index + 1}` : '当前任务'}
                        </div>
                        <SegmentStatusChip segment={{ ...infoSegment, status: task.status, progress: task.progress } as any} />
                      </div>
                      <div className="space-y-1">
                        {progressValue !== undefined ? <Progress value={progressValue} /> : null}
                        <div className="text-[11px] text-muted-foreground break-words">
                          {buildTaskStatusDescription(task, progressValue)}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">生成历史</div>
            {previewEntries.length > 0 ? (
              <div className="text-[11px] text-muted-foreground">
                点击候选卡片可预览，右上角勾选可设为最终视频
              </div>
            ) : null}
          </div>
          {previewEntries.length > 0 ? (
            <div
              ref={previewCarouselRef}
              onWheel={handlePreviewCarouselWheel}
              className="flex gap-2 overflow-x-auto pb-1 pr-1"
              style={{ scrollBehavior: 'smooth' }}
            >
              {previewEntries.map((entry, index) => {
                const isFinal = entry.previewId === selectedPreviewId
                const isActivePreview = entry.previewId === activePreviewId
                const sequenceNumber = previewEntries.length - index
                return (
                  <PreviewEntryCard
                    key={entry.previewId}
                    entry={entry}
                    projectId={infoSegment.projectId}
                    segmentId={infoSegment.segmentId}
                    isFinal={isFinal}
                    isActive={isActivePreview}
                    sequenceNumber={sequenceNumber}
                    mutating={mutating}
                    onPreview={onPreview}
                    onSetFinal={onAdopt}
                    onOpenContextMenu={onOpenContextMenu}
                  />
                )
              })}
            </div>
          ) : (
            <div className="space-y-1">
              <div className="text-sm font-semibold">暂无生成历史</div>
              <div className="text-[11px] text-muted-foreground">
                完成一次生成后，历史视频会出现在这里，点击即可设为最终视频。
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
};

export default SegmentInfoPopover;
